import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Plus, RefreshCcw, DollarSign, TrendingUp, Calendar, CheckCircle, XCircle, FileText, Trash2 } from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useTranslation } from 'react-i18next';
import { useCrmPage, useCrmData, resolveCrmModalTabs } from '../../shared/utils/crmPageUtils';
import { enrichStatusOptions } from '../../shared/utils/crmPageUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import QawaemErpCrudModal from '../../products/saudi/components/QawaemErpCrudModal';
import {
  CrmModalFormTabs,
  CrmModalFormBody,
  CrmModalFormPanel,
  CrmModalFieldGrid,
} from './components/CrmModalFormTabs';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import { useCrmConfig } from './useCrmConfig';
import { filterCrmRecordsByDateRange, sliceCrmListItems } from './crmListPageUtils';
import { useCrmSalesAgentsPicker } from './crmPickerOptions';

const FORECAST_TABLE_COLUMNS = [
  { key: 'ref', labelKey: 'col_ref' },
  { key: 'name', labelKey: 'col_name' },
  { key: 'period', labelKey: 'col_period' },
  { key: 'predicted', labelKey: 'col_predicted' },
  { key: 'actual', labelKey: 'col_actual' },
  { key: 'variance', labelKey: 'col_variance' },
  { key: 'confidence', labelKey: 'col_confidence' },
  { key: 'status', labelKey: 'col_status', align: 'center' },
];

const PERIOD_OPTIONS = [
  { value: 'monthly', labelKey: 'period_monthly' },
  { value: 'quarterly', labelKey: 'period_quarterly' },
  { value: 'yearly', labelKey: 'period_yearly' },
];

const CONFIDENCE_OPTIONS = [
  { value: 'high', labelKey: 'confidence_high' },
  { value: 'medium', labelKey: 'confidence_medium' },
  { value: 'low', labelKey: 'confidence_low' },
];

const emptyForm = () => ({
  forecast_name: '', period: 'monthly', predicted_amount: '', actual_amount: '',
  confidence_level: 'medium', sales_team: '', status: 'active', notes: '',
});

const FORM_ID = 'crm-forecasts-form';
const FORECAST_MODAL_FIELD_COUNT = 8;

const ForecastsPage = () => {
  const { t } = useTranslation();
  const cfgTheme = useModuleConfigTheme();
  const crmConfig = useCrmConfig();
  const { dateRange } = useSaudiListDateRange();
  const { p, fmtMoney } = useCrmPage('forecastsPage');
  const { items: forecasts, load, save, remove } = useCrmData('/crm/forecasting');
  const { agentOptions } = useCrmSalesAgentsPicker();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: entityLabel });

  const [searchTerm, setSearchTerm] = useState('');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [formTab, setFormTab] = useState('forecast');
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(FORECAST_TABLE_COLUMNS.map((c) => [c.key, true]))
  );

  const qawaemColumnDefs = useMemo(
    () => FORECAST_TABLE_COLUMNS.map((col) => ({
      key: col.key,
      label: p(col.labelKey),
      checked: columnVisibility[col.key] !== false,
    })),
    [columnVisibility, p],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const visibleColumns = useMemo(
    () => FORECAST_TABLE_COLUMNS.filter((c) => columnVisibility[c.key] !== false),
    [columnVisibility],
  );
  const tableColSpan = visibleColumns.length + 2;

  const periodLabel = (v) => p(PERIOD_OPTIONS.find((o) => o.value === v)?.labelKey || 'period_monthly');
  const confidenceLabel = (v) => p(CONFIDENCE_OPTIONS.find((o) => o.value === v)?.labelKey || 'confidence_medium');

  const getForecastFilterValue = (f, colKey, varPct) => {
    switch (colKey) {
      case 'ref': return f.forecast_ref || '';
      case 'name': return f.forecast_name || '';
      case 'period': return periodLabel(f.period);
      case 'predicted': return fmtMoney(f.predicted_amount);
      case 'actual': return fmtMoney(f.actual_amount);
      case 'variance': return `${varPct}${varPct !== '—' ? '%' : ''}`;
      case 'confidence': return confidenceLabel(f.confidence_level);
      case 'status': return f.status === 'active' ? (p('status_active') || 'نشط') : (p('status_inactive') || 'غير نشط');
      default: return '';
    }
  };

  const columnFilterRules = useMemo(
    () => visibleColumns.map((col) => {
      const getValue = (row) => {
        const pred = parseFloat(row.predicted_amount) || 0;
        const act = parseFloat(row.actual_amount) || 0;
        const varPct = pred > 0 ? (((act - pred) / pred) * 100).toFixed(1) : '—';
        return getForecastFilterValue(row, col.key, varPct);
      };
      if (col.key === 'status' || col.key === 'period' || col.key === 'confidence') return exactColumnRule(col.key, getValue);
      return textColumnRule(col.key, getValue);
    }),
    [visibleColumns, p, fmtMoney],
  );

  const searchFiltered = useMemo(() => {
    const dated = filterCrmRecordsByDateRange(forecasts, dateRange, ['created_at', 'updated_at', 'start_date']);
    return dated.filter((f) =>
      !searchTerm || f.forecast_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.period?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [forecasts, searchTerm, dateRange]);

  const filtered = useMemo(
    () => applyColumnFilters(searchFiltered, colFilters, columnFilterRules),
    [searchFiltered, colFilters, columnFilterRules],
  );

  const pagedFiltered = useMemo(
    () => sliceCrmListItems(filtered, crmConfig.itemsPerPage),
    [filtered, crmConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filtered);

  useEffect(() => {
    if (selectedRecords.length > 0) setEditingKey(null);
  }, [selectedRecords, setEditingKey]);

  const handleBulkDelete = async () => {
    if (!selectedRecords.length || !window.confirm(p('delete_confirm'))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => remove(id)));
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      setSelectedRecords([]);
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await remove(id);
      notifyDeleted(showNotify, entityLabel);
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  const stats = useMemo(() => {
    const predicted = forecasts.reduce((s, f) => s + (parseFloat(f.predicted_amount) || 0), 0);
    const actual = forecasts.reduce((s, f) => s + (parseFloat(f.actual_amount) || 0), 0);
    const variance = predicted > 0 ? (((actual - predicted) / predicted) * 100).toFixed(1) : '0';
    return { total: forecasts.length, predicted, actual, variance };
  }, [forecasts]);

  const statusOptions = useMemo(() => enrichStatusOptions([
    { value: 'active', label: p('status_active') || 'نشط', icon: CheckCircle },
    { value: 'inactive', label: p('status_inactive') || 'غير نشط', icon: XCircle },
  ]), [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    const forecast = forecasts.find((f) => f.id === recordId);
    if (!forecast) return;
    await save({ ...forecast, status: newStatus }, recordId);
  };

  const renderForecastCell = (f, col, varPct) => {
    switch (col.key) {
      case 'ref':
        return <td key={col.key} className="px-4 py-3 text-white/80 text-sm">{f.forecast_ref || '—'}</td>;
      case 'name':
        return <td key={col.key} className="px-4 py-3 text-white font-medium">{f.forecast_name}</td>;
      case 'period':
        return <td key={col.key} className="px-4 py-3 text-white/70">{periodLabel(f.period)}</td>;
      case 'predicted':
        return <td key={col.key} className="px-4 py-3 text-white font-semibold">{fmtMoney(f.predicted_amount)}</td>;
      case 'actual':
        return <td key={col.key} className="px-4 py-3 text-white/80">{fmtMoney(f.actual_amount)}</td>;
      case 'variance':
        return (
          <td key={col.key} className={`px-4 py-3 font-medium ${parseFloat(varPct) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {varPct}{varPct !== '—' ? '%' : ''}
          </td>
        );
      case 'confidence':
        return <td key={col.key} className="px-4 py-3 text-white/70">{confidenceLabel(f.confidence_level)}</td>;
      case 'status':
        return (
          <td key={col.key} className="px-4 py-3 text-center">
            <div className="flex justify-center">
              <QawaemClickableStatusBadge
                status={f.status || 'active'}
                recordId={f.id}
                options={statusOptions}
                onChange={handleStatusChange}
                title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
              />
            </div>
          </td>
        );
      default:
        return null;
    }
  };

  const forecastFormTabs = useMemo(() => resolveCrmModalTabs(FORECAST_MODAL_FIELD_COUNT, [
    { id: 'forecast', label: p('tab_forecast', { defaultValue: 'التوقع' }), icon: BarChart3 },
    { id: 'amounts', label: p('tab_amounts', { defaultValue: 'المبالغ' }), icon: DollarSign, iconClassName: 'text-green-400' },
    { id: 'notes', label: p('tab_notes', { defaultValue: 'ملاحظات' }), icon: FileText, iconClassName: 'text-purple-400' },
  ]), [p]);
  const forecastDefaultTab = forecastFormTabs[0]?.id || 'forecast';

  const closeModal = () => {
    setFormTab(forecastDefaultTab);
    setShowModal(false);
  };

  const openModal = (record = null) => {
    setEditing(record);
    setForm(record ? { ...record } : emptyForm());
    setFormTab(forecastDefaultTab);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await save(form, editing?.id);
      if (editing?.id) {
        notifyUpdated(showNotify, entityLabel);
      } else {
        notifyCreated(showNotify, entityLabel);
      }
      closeModal();
    } catch {
      notifyError(showNotify, p('save_error'));
    }
  };

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="h-8 w-8 text-indigo-400" />
            {p('title')}
            <button type="button" onClick={load} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
        <button type="button" onClick={() => openModal()}
          className="bg-gradient-to-r from-indigo-500 to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2">
          <Plus size={20} />{p('add')}
        </button>
        {selectedRecords.length > 0 ? (
          <button type="button" onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-200 text-sm font-medium">
            <Trash2 size={16} /><span>{t('common.delete')} ({selectedRecords.length})</span>
          </button>
        ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { title: p('stat_total'), value: stats.total, icon: Calendar },
          { title: p('stat_predicted'), value: fmtMoney(stats.predicted), icon: TrendingUp },
          { title: p('stat_actual'), value: fmtMoney(stats.actual), icon: DollarSign },
          { title: p('stat_variance'), value: `${stats.variance}%`, icon: BarChart3 },
        ].slice(0, 4).map((s, i) => (
          <motion.div key={s.title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white/10 backdrop-blur-lg rounded-2xl p-5 border border-white/20">
            <p className="text-white/60 text-sm">{s.title}</p>
            <p className="text-2xl font-bold text-white mt-1">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="glass-panel glass-table-shell overflow-visible">
        
        <div className="glass-table-scroll overflow-x-auto">
          <table className="w-full employees-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={handleSelectAll}
                />
                {visibleColumns.map((col) => (
                  <TableColumnHeaderFilter
                    key={col.key}
                    columnKey={col.key}
                    label={p(col.labelKey)}
                    kind={col.key === 'status' || col.key === 'period' || col.key === 'confidence' ? 'select' : 'text'}
                    value={colFilters[col.key] ?? ''}
                    onChange={(v) => setColFilter(col.key, v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    align={col.align === 'center' ? 'center' : undefined}
                    thClassName="text-white font-semibold text-sm"
                    className="px-4 py-3"
                  />
                ))}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={qawaemColumnDefs}
                  onToggle={handleToggleColumn}
                  visibleCount={qawaemVisibleColumnCount}
                  totalCount={qawaemColumnDefs.length}
                  thClassName="px-4 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filtered.length ? pagedFiltered.map((f) => {
                const pred = parseFloat(f.predicted_amount) || 0;
                const act = parseFloat(f.actual_amount) || 0;
                const varPct = pred > 0 ? (((act - pred) / pred) * 100).toFixed(1) : '—';
                return (
                  <tr key={f.id} className={`hover:bg-white/5 ${selectedRecords.includes(f.id) ? 'bg-white/10' : ''}`}>
                    <QawaemTableSelectRowCell
                      checked={selectedRecords.includes(f.id)}
                      onChange={() => handleSelectRecord(f.id)}
                    />
                    {visibleColumns.map((col) => renderForecastCell(f, col, varPct))}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onEdit={() => openModal(f)}
                        onDelete={() => handleDelete(f.id)}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                );
              }) : (
                <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{p('empty')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={closeModal}
          title={editing ? p('modal_edit') : p('modal_new')}
          icon={BarChart3}
          formId={FORM_ID}
          saveLabel={t('common.save')}
          onSubmit
          maxWidth="1200px"
          testId="crm-forecasts-modal"
        >
          {forecastFormTabs.length > 0 ? (
            <CrmModalFormBody>
              <CrmModalFormTabs
                tabs={forecastFormTabs}
                activeTab={formTab}
                onTabChange={setFormTab}
                ariaLabel={editing ? p('modal_edit') : p('modal_new')}
              />
              <CrmModalFormPanel>
                <form id={FORM_ID} onSubmit={handleSubmit}>
                  {formTab === 'forecast' && (
                    <CrmModalFieldGrid>
                      <div>
                        <label className={cfgTheme.label}>{p('field_name')}<span className="text-red-600 font-bold"> *</span></label>
                        <input required value={form.forecast_name} onChange={(e) => setForm({ ...form, forecast_name: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_period')}</label>
                        <CustomSelect value={form.period} onChange={(v) => setForm({ ...form, period: v })}
                          options={PERIOD_OPTIONS.map((o) => ({ value: o.value, label: p(o.labelKey) }))} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_confidence')}</label>
                        <CustomSelect value={form.confidence_level} onChange={(v) => setForm({ ...form, confidence_level: v })}
                          options={CONFIDENCE_OPTIONS.map((o) => ({ value: o.value, label: p(o.labelKey) }))} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('col_status')}</label>
                        <CustomSelect value={form.status || 'active'} onChange={(v) => setForm({ ...form, status: v })}
                          options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_team')}</label>
                        <CustomSelect
                          value={form.sales_team || ''}
                          onChange={(v) => setForm({ ...form, sales_team: v })}
                          options={agentOptions}
                          placeholder={p('field_team_ph', { defaultValue: 'اختر مندوب مبيعات' })}
                        />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                  {formTab === 'amounts' && (
                    <CrmModalFieldGrid>
                      <div>
                        <label className={cfgTheme.label}>{p('field_predicted')}</label>
                        <input type="number" step="0.01" value={form.predicted_amount} onChange={(e) => setForm({ ...form, predicted_amount: e.target.value })} className={cfgTheme.input} />
                      </div>
                      <div>
                        <label className={cfgTheme.label}>{p('field_actual')}</label>
                        <input type="number" step="0.01" value={form.actual_amount} onChange={(e) => setForm({ ...form, actual_amount: e.target.value })} className={cfgTheme.input} />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                  {formTab === 'notes' && (
                    <CrmModalFieldGrid>
                      <div className="md:col-span-3">
                        <label className={cfgTheme.label}>{p('field_notes', { defaultValue: 'ملاحظات' })}</label>
                        <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className={`${cfgTheme.input} resize-none`} />
                      </div>
                    </CrmModalFieldGrid>
                  )}
                </form>
              </CrmModalFormPanel>
            </CrmModalFormBody>
          ) : (
            <form id={FORM_ID} onSubmit={handleSubmit}>
              <CrmModalFieldGrid>
                <div>
                  <label className={cfgTheme.label}>{p('field_name')}<span className="text-red-600 font-bold"> *</span></label>
                  <input required value={form.forecast_name} onChange={(e) => setForm({ ...form, forecast_name: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_period')}</label>
                  <CustomSelect value={form.period} onChange={(v) => setForm({ ...form, period: v })}
                    options={PERIOD_OPTIONS.map((o) => ({ value: o.value, label: p(o.labelKey) }))} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_confidence')}</label>
                  <CustomSelect value={form.confidence_level} onChange={(v) => setForm({ ...form, confidence_level: v })}
                    options={CONFIDENCE_OPTIONS.map((o) => ({ value: o.value, label: p(o.labelKey) }))} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('col_status')}</label>
                  <CustomSelect value={form.status || 'active'} onChange={(v) => setForm({ ...form, status: v })}
                    options={statusOptions.map((s) => ({ value: s.value, label: s.label }))} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_team')}</label>
                  <CustomSelect
                    value={form.sales_team || ''}
                    onChange={(v) => setForm({ ...form, sales_team: v })}
                    options={agentOptions}
                    placeholder={p('field_team_ph', { defaultValue: 'اختر مندوب مبيعات' })}
                  />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_predicted')}</label>
                  <input type="number" step="0.01" value={form.predicted_amount} onChange={(e) => setForm({ ...form, predicted_amount: e.target.value })} className={cfgTheme.input} />
                </div>
                <div>
                  <label className={cfgTheme.label}>{p('field_actual')}</label>
                  <input type="number" step="0.01" value={form.actual_amount} onChange={(e) => setForm({ ...form, actual_amount: e.target.value })} className={cfgTheme.input} />
                </div>
                <div className="md:col-span-3">
                  <label className={cfgTheme.label}>{p('field_notes', { defaultValue: 'ملاحظات' })}</label>
                  <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} className={`${cfgTheme.input} resize-none`} />
                </div>
              </CrmModalFieldGrid>
            </form>
          )}
        </QawaemErpCrudModal>
      )}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default ForecastsPage;
